/**
 * Takes the verification token out of the URL fragment on `/auth/email-verify`.
 *
 * The same two jobs `takeTicketFromFragment` does in `use-ticket-sign-in.ts` for Clerk tickets,
 * kept separate because this token is not a Clerk ticket and must never reach that hook:
 *
 *   1. read the token from the fragment, where the link builder put it so servers never log it;
 *   2. remove it from the address bar straight away, before telemetry that samples the URL, a
 *      bookmark or a shared screenshot can pick it up.
 *
 * Taking is destructive — a second read finds nothing — and React.StrictMode double-invokes state
 * initialisers, so `captureVerifyTokenOnce` holds the token for the life of the page load.
 */

import { ADVISOR_VERIFY_TOKEN_PARAM } from '@shared';

type FragmentLocation = Pick<Location, 'hash' | 'pathname' | 'search'>;
type FragmentHistory = Pick<History, 'replaceState' | 'state'>;

export function takeVerifyTokenFromFragment(
  location: FragmentLocation = window.location,
  history: FragmentHistory = window.history,
): string | null {
  const hash = location.hash.startsWith('#') ? location.hash.slice(1) : '';
  if (!hash) return null;

  const params = new URLSearchParams(hash);
  const token = params.get(ADVISOR_VERIFY_TOKEN_PARAM);
  if (!token) return null;

  params.delete(ADVISOR_VERIFY_TOKEN_PARAM);
  const remaining = params.toString();
  history.replaceState(
    history.state,
    '',
    `${location.pathname}${location.search}${remaining ? `#${remaining}` : ''}`,
  );

  return token;
}

let capturedToken: string | null | undefined;

/**
 * The token for this page load, read once.
 *
 * A later call that finds a NEW token in the fragment — a second link opened in the same tab —
 * replaces the held one, so the page always acts on the link the advisor most recently opened.
 */
export function captureVerifyTokenOnce(): string | null {
  const fresh = takeVerifyTokenFromFragment();

  if (fresh) {
    capturedToken = fresh;
  } else if (capturedToken === undefined) {
    capturedToken = null;
  }

  return capturedToken;
}
